import { useCallback } from 'react';
import { useArtsUpload } from '~/adapters/services/accounts/arts/hooks';
import { useAccount } from '~/contexts/UserAccount/UserAccountContext';
import { pushErrorNotification } from '~/adapters/notistack/notistack';
import { FileRejection } from 'react-dropzone';

// Browser-compatible Windows detection
const isWindows = (): boolean => {
    return typeof window !== 'undefined' && /Win/.test(navigator.userAgent);
};

const BASE_ACCEPTED_FILE_TYPES: Record<string, string[]> = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/bmp': ['.bmp'],
    'image/tiff': ['.tif', '.tiff'],
    'image/webp': ['.webp'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'image/avif': ['.avif'],
    'application/pdf': ['.pdf'],
    'application/x-pdf': ['.pdf'],
    'application/postscript': ['.eps'],
    'image/x-eps': ['.eps'],
    'application/vnd.adobe.illustrator': ['.ai'],
    'application/illustrator': ['.ai'],
    'image/vnd.adobe.illustrator': ['.ai'],
    'application/vnd.adobe.photoshop': ['.psd'],
    'application/photoshop': ['.psd'],
    'image/vnd.adobe.photoshop': ['.psd'],
    'image/svg+xml': ['.svg'],
};

/*
 * Function to get accepted file types, excluding HEIC on Windows
 *
 * HEIC is Apple proprietary format, altho supported on Windows, it's MIME is application/octet-stream.
 * - Nest.js 10 (current version we use) does work purely with MIME.
 * - Nest.js 11 uses magic numbers from header for detection and thus it will work once we upgrade.
 * Adding support for Magic detection in AS is overkill.
 * It works in Upload Service (NDX) as that uses WASMagic/Magic numbers to detect the file type.
 */
export const getAcceptedFileTypes = () => {
    if (isWindows()) {
        // Remove HEIC and HEIF support on Windows
        const { 'image/heic': heic, 'image/heif': heif, ...acceptedTypes } = BASE_ACCEPTED_FILE_TYPES;
        return acceptedTypes;
    }
    return BASE_ACCEPTED_FILE_TYPES;
};

// Export for backward compatibility and external usage
export const ACCEPTED_FILE_TYPES = getAcceptedFileTypes();
export const FILE_MAX_SIZE_IN_BYTES = 5_000_000; // in bytes

export const useUpload = () => {
    const { mutate, isPending } = useArtsUpload();
    const currentAccount = useAccount();

    const handleRejectedFiles = useCallback((rejectedFiles: FileRejection[]) => {
        rejectedFiles.forEach((rejectedFile) => {
            let message = `File ${rejectedFile.file.name} failed to upload.`;

            if (rejectedFile.errors.some((err) => err.code === 'file-too-large')) {
                message += ` File is too large, please use file with maximal size ${(
                    FILE_MAX_SIZE_IN_BYTES / 1e6
                ).toFixed()} Mb.`;
            }

            if (rejectedFile.errors.some((err) => err.code === 'file-invalid-type')) {
                message += ` File has invalid type.`;
            }
            pushErrorNotification(<span data-action="file-too-large-alert">{message}</span>);
        });
    }, []);

    const onUpload = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            handleRejectedFiles(rejectedFiles);
            mutate({ files: acceptedFiles, accountId: currentAccount.id });
        },
        [mutate, currentAccount.id, handleRejectedFiles],
    );

    return {
        isPending,
        onUpload,
    };
};

// Helper function to check if a file type is accepted
export const isFileTypeAccepted = (file: File) => {
    const mimeType = file.type.toLowerCase();
    const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : '';
    // Check if the MIME type is directly accepted
    if (ACCEPTED_FILE_TYPES[mimeType]) {
        return ACCEPTED_FILE_TYPES[mimeType].includes(extension);
    }

    // Check if any MIME type accepts this extension
    return Object.values(ACCEPTED_FILE_TYPES).some((extensions) => extensions.includes(extension));
};
